import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  Textarea,
  useToast,
  VStack,
  FormErrorMessage,
} from "@chakra-ui/react";
import { fetchSignInMethodsForEmail } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { collection, getDocs, limit, query } from "firebase/firestore";
import { auth, db, functions } from "../../firebase/config";
import { Store } from "../type/Store";
import { BillingSubactivity, billingSubactivityOf, CreatedStorePins, createStoreRecord, OrganizationChoice } from "./createStoreRecord";
import { lookupGooglePlace } from "./lookupGooglePlace";

type OrgRow = {
  id: string;
  type: "headquarters" | "legalEntity" | "brand" | "operator";
  name: string;
};

const ORG_TYPES = ["headquarters", "legalEntity", "brand", "operator"] as const;

const ORG_LABEL: Record<(typeof ORG_TYPES)[number], string> = {
  headquarters: "Headquarters",
  legalEntity: "Legal entity",
  brand: "Brand",
  operator: "Operator",
};

function staffSlug(storeName: string) {
  const words = storeName.trim().split(/\s+/).filter(Boolean);
  const piece = words.length >= 3
    ? `${words[0]}${words[1]}${words[2].charAt(0)}`
    : words.join("");
  return piece
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

function staffFromStoreName(storeName: string) {
  const words = storeName.trim().split(/\s+/).filter(Boolean);
  const firstName = words.length >= 3
    ? `${words[0]} ${words[1]} ${words[2].charAt(0)}`
    : words.join(" ");
  const slug = staffSlug(storeName);
  return {
    firstName,
    adminLastName: "Admin",
    managerLastName: "mgr1",
    adminEmail: slug ? `${slug}-admin@nuagepos.com` : "",
    managerEmail: slug ? `${slug}-mgr1@nuagepos.com` : "",
  };
}

async function emailRegisteredInAuth(email: string) {
  const methods = await fetchSignInMethodsForEmail(auth, email.trim().toLowerCase());
  return methods.length > 0;
}

function escapeHtml(value: string) {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function pinPeople(pins: CreatedStorePins) {
  return [
    { role: "Store owner", name: pins.ownerName, email: pins.ownerEmail, pin: pins.ownerPin },
    { role: "Store admin", name: pins.adminName, email: pins.adminEmail, pin: pins.adminPin },
    { role: "Manager", name: pins.managerName, email: pins.managerEmail, pin: pins.managerPin },
  ];
}

function pinRecord(pins: CreatedStorePins) {
  const heading = pins.storeName ? `${pins.storeName}\n\n` : "";
  const lines = pinPeople(pins).map((person) => (
    `${person.role}\n${person.name}\n${person.email}\nPIN ${person.pin}`
  ));
  return `${heading}${lines.join("\n\n")}`;
}

function pinMessages(pin: string) {
  return [
    "Your store is being created.",
    `Your store PIN is ${pin}.`,
    pin,
  ];
}

async function sendPlainEmail(to: string, toName: string, subject: string, text: string) {
  const sendConfiguredEmail = httpsCallable<
    { to: string; toName: string; subject: string; html: string; text: string },
    { ok?: boolean; message?: string }
  >(functions, "sendConfiguredEmail");
  const { data } = await sendConfiguredEmail({
    to,
    toName,
    subject,
    html: `<p>${escapeHtml(text).replace(/\n/g, "<br>")}</p>`,
    text,
  });
  if (!data?.ok) throw new Error(data?.message || "Email was not sent");
}

type CreateStoreDialogProps = {
  open: boolean;
  stores: Store[];
  ownerEmail: string;
  onClose: () => void;
};

const CreateStoreDialog: React.FC<CreateStoreDialogProps> = ({ open, stores, ownerEmail, onClose }) => {
  const toast = useToast();
  const [start, setStart] = useState<"blank" | "clone">("blank");
  const [sourceId, setSourceId] = useState("");
  const [mapsUrl, setMapsUrl] = useState("");
  const [name, setName] = useState("");
  const [billingSubactivity, setBillingSubactivity] = useState<BillingSubactivity>("RES");
  const [activityWord, setActivityWord] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [adminFirstName, setAdminFirstName] = useState("");
  const [adminLastName, setAdminLastName] = useState("Admin");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminEmailError, setAdminEmailError] = useState("");
  const [managerFirstName, setManagerFirstName] = useState("");
  const [managerLastName, setManagerLastName] = useState("mgr1");
  const [managerEmail, setManagerEmail] = useState("");
  const [managerEmailError, setManagerEmailError] = useState("");
  const staffAuto = useRef(staffFromStoreName(""));
  const [orgMode, setOrgMode] = useState<"skip" | "existing" | "create">("skip");
  const [orgs, setOrgs] = useState<OrgRow[]>([]);
  const [orgId, setOrgId] = useState("");
  const [newOrgType, setNewOrgType] = useState<(typeof ORG_TYPES)[number]>("headquarters");
  const [newOrgName, setNewOrgName] = useState("");
  const [busy, setBusy] = useState(false);
  const [readingMap, setReadingMap] = useState(false);
  const [pins, setPins] = useState<CreatedStorePins | null>(null);
  const [sendingKey, setSendingKey] = useState("");
  const mailedStoreId = useRef("");

  useEffect(() => {
    const next = staffFromStoreName(name);
    const previous = staffAuto.current;
    const keep = (current: string, prior: string) => (current === prior ? undefined : current);
    setAdminFirstName((current) => keep(current, previous.firstName) ?? next.firstName);
    setAdminLastName((current) => keep(current, previous.adminLastName) ?? next.adminLastName);
    setAdminEmail((current) => keep(current, previous.adminEmail) ?? next.adminEmail);
    setManagerFirstName((current) => keep(current, previous.firstName) ?? next.firstName);
    setManagerLastName((current) => keep(current, previous.managerLastName) ?? next.managerLastName);
    setManagerEmail((current) => keep(current, previous.managerEmail) ?? next.managerEmail);
    setAdminEmailError("");
    setManagerEmailError("");
    staffAuto.current = next;
  }, [name]);

  useEffect(() => {
    if (!pins || mailedStoreId.current === pins.storeId) return;
    mailedStoreId.current = pins.storeId;
    const record = pinRecord(pins);
    sendPlainEmail(pins.ownerEmail, pins.ownerName, "Your store PINs", record)
      .then(() => {
        toast({ title: "PINs sent", description: `Sent to ${pins.ownerEmail}.`, status: "success" });
      })
      .catch((error: unknown) => {
        toast({
          title: "The owner email was not sent",
          description: error instanceof Error ? error.message : "Try the send buttons.",
          status: "error",
        });
      });
  }, [pins, toast]);

  const activityBaseline: BillingSubactivity = start === "clone"
    ? billingSubactivityOf(stores.find((item) => item.id === sourceId)?.billingSubactivity)
    : "RES";

  useEffect(() => {
    setBillingSubactivity(activityBaseline);
    setActivityWord("");
  }, [activityBaseline]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    getDocs(query(collection(db, "organizations"), limit(200))).then((snap) => {
      if (cancelled) return;
      const rows = snap.docs.flatMap((item) => {
        const data = item.data() as { type?: string; name?: string };
        if (!ORG_TYPES.includes(data.type as (typeof ORG_TYPES)[number])) return [];
        return [{ id: item.id, type: data.type as OrgRow["type"], name: data.name || item.id }];
      });
      rows.sort((left, right) => left.name.localeCompare(right.name));
      setOrgs(rows);
    }).catch(() => {
      if (!cancelled) setOrgs([]);
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const applyMaps = async (value: string) => {
    setReadingMap(true);
    try {
      const place = await lookupGooglePlace(value);
      if (place.name) setName(place.name);
      if (place.address) setAddress(place.address);
      if (place.phone) setPhone(place.phone);
      if (place.latitude) setLatitude(place.latitude);
      if (place.longitude) setLongitude(place.longitude);
      if (!place.address || !place.phone) {
        toast({
          title: "Google Maps link",
          description: !place.address && !place.phone
            ? "The place name was read. The address and phone were not found for this link."
            : !place.address
              ? "The phone was read. The street address was not found for this link."
              : "The address was read. The phone was not found for this link.",
          status: "warning",
        });
      }
    } catch (error) {
      toast({
        title: "Google Maps link",
        description: error instanceof Error ? error.message : "The link could not be read.",
        status: "error",
      });
    } finally {
      setReadingMap(false);
    }
  };

  const reset = () => {
    setStart("blank");
    setSourceId("");
    setMapsUrl("");
    setName("");
    setBillingSubactivity("RES");
    setActivityWord("");
    setAddress("");
    setPhone("");
    setEmail("");
    setLatitude("");
    setLongitude("");
    setAdminFirstName("");
    setAdminLastName("Admin");
    setAdminEmail("");
    setAdminEmailError("");
    setManagerFirstName("");
    setManagerLastName("mgr1");
    setManagerEmail("");
    setManagerEmailError("");
    staffAuto.current = staffFromStoreName("");
    setOrgMode("skip");
    setOrgId("");
    setNewOrgType("headquarters");
    setNewOrgName("");
    setPins(null);
    setSendingKey("");
    mailedStoreId.current = "";
    setBusy(false);
    setReadingMap(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const submit = async () => {
    if (start === "clone" && !sourceId) {
      toast({ title: "Choose a store to clone", status: "error" });
      return;
    }
    if (billingSubactivity !== activityBaseline && activityWord.trim() !== "change") {
      toast({ title: "Type change to confirm the store activity", status: "error" });
      return;
    }
    if (!adminFirstName.trim() || !adminEmail.trim()) {
      toast({ title: "Enter the store admin name and email", status: "error" });
      return;
    }
    if (!managerFirstName.trim() || !managerEmail.trim()) {
      toast({ title: "Enter the manager name and email", status: "error" });
      return;
    }
    const adminAddress = adminEmail.trim().toLowerCase();
    const managerAddress = managerEmail.trim().toLowerCase();
    if (adminAddress === managerAddress) {
      const message = "This email is already used. Change the email.";
      setAdminEmailError(message);
      setManagerEmailError(message);
      toast({ title: "Change one of the emails", description: "The admin and the manager cannot use the same email.", status: "error" });
      return;
    }
    const ownerAddress = ownerEmail.trim().toLowerCase();
    if (adminAddress === ownerAddress || managerAddress === ownerAddress) {
      if (adminAddress === ownerAddress) setAdminEmailError("This email is already used in Authentication. Change the admin email.");
      if (managerAddress === ownerAddress) setManagerEmailError("This email is already used in Authentication. Change the manager email.");
      toast({ title: "Change the email", description: "That address already has an account.", status: "error" });
      return;
    }
    let organization: OrganizationChoice = { mode: "skip" };
    if (orgMode === "existing") {
      const selected = orgs.find((item) => item.id === orgId);
      if (!selected) {
        toast({ title: "Choose an organization", status: "error" });
        return;
      }
      organization = { mode: "existing", type: selected.type, id: selected.id };
    }
    if (orgMode === "create") {
      organization = { mode: "create", type: newOrgType, name: newOrgName };
    }
    setBusy(true);
    try {
      const [adminTaken, managerTaken] = await Promise.all([
        emailRegisteredInAuth(adminAddress),
        emailRegisteredInAuth(managerAddress),
      ]);
      if (adminTaken || managerTaken) {
        if (adminTaken) setAdminEmailError("This email is already used in Authentication. Change the admin email.");
        else setAdminEmailError("");
        if (managerTaken) setManagerEmailError("This email is already used in Authentication. Change the manager email.");
        else setManagerEmailError("");
        toast({ title: "Change the email", description: "That address already has an account.", status: "error" });
        return;
      }
      setAdminEmailError("");
      setManagerEmailError("");
      const created = await createStoreRecord({
        ownerEmail,
        name,
        billingSubactivity,
        address,
        phone,
        email,
        currency: "CAD",
        latitude,
        longitude,
        adminFirstName,
        adminLastName,
        adminEmail,
        managerFirstName,
        managerLastName,
        managerEmail,
        sourceStoreId: start === "clone" ? sourceId : "",
        organization,
      });
      setPins(created);
    } catch (error) {
      toast({
        title: "Store was not created",
        description: error instanceof Error ? error.message : "Try again.",
        status: "error",
      });
    } finally {
      setBusy(false);
    }
  };

  const copyPins = async () => {
    if (!pins) return;
    try {
      await navigator.clipboard.writeText(pinRecord(pins));
      toast({ title: "Copied", description: "The names, emails, and PINs are on the clipboard.", status: "success" });
    } catch (error) {
      toast({
        title: "Copy failed",
        description: error instanceof Error ? error.message : "Copy the PINs from the dialog.",
        status: "error",
      });
    }
  };

  const sendTest = async (emailAddress: string, personName: string, text: string) => {
    const key = `${emailAddress}|${text}`;
    setSendingKey(key);
    try {
      await sendPlainEmail(emailAddress, personName, text, text);
      toast({ title: "Email sent", description: `Sent to ${emailAddress}.`, status: "success" });
    } catch (error) {
      toast({
        title: "Email was not sent",
        description: error instanceof Error ? error.message : "Try again.",
        status: "error",
      });
    } finally {
      setSendingKey("");
    }
  };

  return (
    <Modal isOpen={open} onClose={close} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{pins ? "Store created" : "Create new store"}</ModalHeader>
        <ModalBody>
          {pins ? (
            <VStack align="stretch" spacing={4}>
              <Text>The store owner, the store admin, and the manager can sign in to the POS with these PINs. The store owner receives an email with every PIN.</Text>
              {pinPeople(pins).map((person) => (
                <VStack key={person.role} align="stretch" spacing={2}>
                  <Text><Text as="span" fontWeight="700">{person.name}</Text> · {person.role} · PIN {person.pin}</Text>
                  <Text>{person.email}</Text>
                  {pinMessages(person.pin).map((text) => (
                    <Button
                      key={text}
                      variant="outline"
                      justifyContent="flex-start"
                      whiteSpace="normal"
                      height="auto"
                      py={3}
                      isLoading={sendingKey === `${person.email}|${text}`}
                      isDisabled={Boolean(sendingKey)}
                      onClick={() => sendTest(person.email, person.name, text)}
                    >
                      {text}
                    </Button>
                  ))}
                </VStack>
              ))}
              {pins.menuWarning ? <Text color="red.600">{pins.menuWarning}</Text> : null}
            </VStack>
          ) : (
            <VStack align="stretch" spacing={3}>
              <FormControl>
                <FormLabel>Start from</FormLabel>
                <Select value={start} onChange={(event) => setStart(event.target.value as "blank" | "clone")}>
                  <option value="blank">Empty store</option>
                  <option value="clone">Clone an existing store</option>
                </Select>
              </FormControl>
              {start === "clone" && (
                <FormControl>
                  <FormLabel>Store to clone</FormLabel>
                  <Select placeholder="Choose a store" value={sourceId} onChange={(event) => setSourceId(event.target.value)}>
                    {[...stores].sort((left, right) => (left.name || "").localeCompare(right.name || "")).map((store) => (
                      <option key={store.id} value={store.id}>{store.name || store.id}</option>
                    ))}
                  </Select>
                </FormControl>
              )}
              <FormControl>
                <FormLabel>Google Maps link{readingMap ? " — reading address and phone" : ""}</FormLabel>
                <Input
                  value={mapsUrl}
                  placeholder="Paste a full Google Maps link"
                  onChange={(event) => setMapsUrl(event.target.value)}
                  onBlur={() => {
                    if (mapsUrl.trim()) applyMaps(mapsUrl);
                  }}
                  onPaste={(event) => {
                    const pasted = event.clipboardData.getData("text");
                    if (pasted.trim()) {
                      setMapsUrl(pasted);
                      applyMaps(pasted);
                    }
                  }}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Store name</FormLabel>
                <Input value={name} onChange={(event) => setName(event.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel>Address</FormLabel>
                <Textarea value={address} onChange={(event) => setAddress(event.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel>Phone</FormLabel>
                <Input value={phone} onChange={(event) => setPhone(event.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel>Store email</FormLabel>
                <Input value={email} onChange={(event) => setEmail(event.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel>Currency</FormLabel>
                <Input value="CAD" isReadOnly />
              </FormControl>
              <FormControl>
                <FormLabel>Store activity</FormLabel>
                <Select
                  value={billingSubactivity}
                  onChange={(event) => {
                    setBillingSubactivity(billingSubactivityOf(event.target.value));
                    setActivityWord("");
                  }}
                >
                  <option value="RES">Restaurant</option>
                  <option value="BAR">Bar</option>
                  <option value="CDR">Food truck</option>
                </Select>
                <Text fontSize="sm" color="gray.600" mt={2}>
                  Restaurant is the default. Bills use this activity for each priced item.
                </Text>
                {billingSubactivity !== activityBaseline && (
                  <Input
                    mt={3}
                    value={activityWord}
                    onChange={(event) => setActivityWord(event.target.value)}
                    placeholder="Type change to confirm"
                    autoComplete="off"
                  />
                )}
              </FormControl>
              <FormControl>
                <FormLabel>Admin first name</FormLabel>
                <Input value={adminFirstName} onChange={(event) => setAdminFirstName(event.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel>Admin last name</FormLabel>
                <Input value={adminLastName} onChange={(event) => setAdminLastName(event.target.value)} />
              </FormControl>
              <FormControl isInvalid={Boolean(adminEmailError)}>
                <FormLabel>Admin email</FormLabel>
                <Input value={adminEmail} onChange={(event) => { setAdminEmail(event.target.value); setAdminEmailError(""); }} />
                <FormErrorMessage>{adminEmailError}</FormErrorMessage>
              </FormControl>
              <FormControl>
                <FormLabel>Manager first name</FormLabel>
                <Input value={managerFirstName} onChange={(event) => setManagerFirstName(event.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel>Manager last name</FormLabel>
                <Input value={managerLastName} onChange={(event) => setManagerLastName(event.target.value)} />
              </FormControl>
              <FormControl isInvalid={Boolean(managerEmailError)}>
                <FormLabel>Manager email</FormLabel>
                <Input value={managerEmail} onChange={(event) => { setManagerEmail(event.target.value); setManagerEmailError(""); }} />
                <FormErrorMessage>{managerEmailError}</FormErrorMessage>
              </FormControl>
              <FormControl>
                <FormLabel>Organization</FormLabel>
                <Select value={orgMode} onChange={(event) => setOrgMode(event.target.value as "skip" | "existing" | "create")}>
                  <option value="skip">Skip for a single store</option>
                  <option value="existing">Choose an existing organization</option>
                  <option value="create">Create an organization</option>
                </Select>
              </FormControl>
              {orgMode === "existing" && (
                <FormControl>
                  <FormLabel>Existing organization</FormLabel>
                  <Select placeholder="Choose an organization" value={orgId} onChange={(event) => setOrgId(event.target.value)}>
                    {orgs.map((org) => (
                      <option key={org.id} value={org.id}>{ORG_LABEL[org.type]} · {org.name}</option>
                    ))}
                  </Select>
                </FormControl>
              )}
              {orgMode === "create" && (
                <>
                  <FormControl>
                    <FormLabel>Organization level</FormLabel>
                    <Select value={newOrgType} onChange={(event) => setNewOrgType(event.target.value as (typeof ORG_TYPES)[number])}>
                      {ORG_TYPES.map((type) => (
                        <option key={type} value={type}>{ORG_LABEL[type]}</option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Organization name</FormLabel>
                    <Input value={newOrgName} onChange={(event) => setNewOrgName(event.target.value)} />
                  </FormControl>
                </>
              )}
              <Text fontSize="sm" color="gray.600">
                You remain the store owner. The admin is a store admin. The manager is a member. This does not create a Firebase login.
              </Text>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          {pins ? (
            <>
              <Button variant="outline" mr={3} onClick={copyPins}>Copy</Button>
              <Button colorScheme="blue" onClick={close}>Close</Button>
            </>
          ) : (
            <>
              <Button variant="outline" mr={3} onClick={close} isDisabled={busy}>Cancel</Button>
              <Button colorScheme="blue" onClick={submit} isLoading={busy}>Create new store</Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CreateStoreDialog;
